// src/features/notifications/hooks/useNotifications.ts
// Gate R2 Notifications B/C — recipient inbox over public.notifications
// (delivery + user state). Does not read security_events as the inbox.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isDemoMode } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  isSecurityDerivedNotificationType,
  type NotificationDeliveryStatus,
} from '@/services/policies/notificationDeliveryPolicy';
import {
  getDemoParticipantNotifications,
  subscribeDemoProjectParticipants,
  updateDemoParticipantNotification,
} from '@/demo/demoProjectParticipantStore';
import { nextPostgresChangesChannelTopic } from '@/lib/realtime/postgresChangesChannelTopic';
import type { User } from '@/types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type NotificationType =
  | 'chat_mention'
  | 'thread_mention'
  | 'room_invite'
  | 'reaction'
  | 'call_started'
  | 'call_missed'
  | 'call_reminder'
  | 'decision_requested'
  | 'decision_action_assigned'
  | 'decision_recorded'
  | 'security_event'
  | string;

export interface Notification {
  id: string;
  user_id: string;
  actor_id: string | null;
  type: NotificationType;
  title: string;
  body: string;
  room_id: string | null;
  message_id: string | null;
  thread_parent_id: string | null;
  call_session_id?: string | null;
  decision_id?: string | null;
  preview: string | null;
  created_at: string;
  read_at: string | null;
  dismissed_at: string | null;
  archived_at: string | null;
  delivery_status: NotificationDeliveryStatus;
  source_security_event_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown>;
  // Joined / derived display
  actor_name?: string;
  actor_avatar?: string;
  room_name?: string;
}

export interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markManyAsRead: (notificationIds: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (notificationId: string) => Promise<void>;
  archive: (notificationId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

type NotificationRow = {
  id: string;
  user_id: string;
  actor_id: string | null;
  type: string;
  title: string | null;
  body: string | null;
  preview: string | null;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  read_at: string | null;
  dismissed_at: string | null;
  archived_at: string | null;
  delivery_status: NotificationDeliveryStatus | null;
  source_security_event_id: string | null;
  channel?: string | null;
};

function mapRow(row: NotificationRow): Notification {
  const metadata = (row.metadata && typeof row.metadata === 'object' ? row.metadata : {}) as Record<
    string,
    unknown
  >;
  const preview =
    row.preview ||
    row.body ||
    (typeof metadata.preview === 'string' ? metadata.preview : null);

  return {
    id: row.id,
    user_id: row.user_id,
    actor_id: row.actor_id,
    type: row.type,
    title: row.title || row.type,
    body: row.body || '',
    room_id: typeof metadata.room_id === 'string' ? metadata.room_id : null,
    message_id: typeof metadata.message_id === 'string' ? metadata.message_id : null,
    thread_parent_id:
      typeof metadata.thread_parent_id === 'string' ? metadata.thread_parent_id : null,
    call_session_id:
      typeof metadata.call_session_id === 'string' ? metadata.call_session_id : null,
    decision_id:
      row.entity_type === 'decision'
        ? row.entity_id
        : typeof metadata.decision_id === 'string'
          ? metadata.decision_id
          : null,
    preview,
    created_at: row.created_at,
    read_at: row.read_at,
    dismissed_at: row.dismissed_at,
    archived_at: row.archived_at,
    delivery_status: row.delivery_status || 'delivered',
    source_security_event_id: row.source_security_event_id,
    entity_type: row.entity_type,
    entity_id: row.entity_id,
    metadata,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Demo Data — only when isDemoMode (never as fallback for failed production reads)
// ─────────────────────────────────────────────────────────────────────────────

const DEMO_NOTIFICATIONS: Notification[] = [
  {
    id: 'demo-notif-1',
    user_id: 'demo-user',
    actor_id: '3',
    type: 'room_invite',
    title: 'Chat Invite',
    body: 'You were added to "Team Weekly Sync"',
    room_id: '4',
    message_id: null,
    thread_parent_id: null,
    preview: 'You were added to "Team Weekly Sync"',
    created_at: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    read_at: null,
    dismissed_at: null,
    archived_at: null,
    delivery_status: 'delivered',
    source_security_event_id: null,
    entity_type: null,
    entity_id: null,
    metadata: { room_id: '4' },
    actor_name: 'Emily Vendor Lead',
    room_name: 'Team Weekly Sync',
  },
  {
    id: 'demo-notif-sec-1',
    user_id: 'demo-user',
    actor_id: null,
    type: 'api_key_created',
    title: 'Api Key Created',
    body: 'A new API key was created in your workspace',
    room_id: null,
    message_id: null,
    thread_parent_id: null,
    preview: 'A new API key was created in your workspace',
    created_at: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
    read_at: null,
    dismissed_at: null,
    archived_at: null,
    delivery_status: 'delivered',
    source_security_event_id: 'demo-sec-event-1',
    entity_type: 'security_event',
    entity_id: 'demo-sec-event-1',
    metadata: { source: 'security_events', severity: 'info' },
  },
  {
    id: 'demo-notif-2',
    user_id: 'demo-user',
    actor_id: '6',
    type: 'reaction',
    title: 'Reaction',
    body: '👍 reacted to your message',
    room_id: '3',
    message_id: 'demo-msg-3',
    thread_parent_id: null,
    preview: '👍 reacted to your message',
    created_at: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    read_at: null,
    dismissed_at: null,
    archived_at: null,
    delivery_status: 'delivered',
    source_security_event_id: null,
    entity_type: null,
    entity_id: null,
    metadata: { room_id: '3', message_id: 'demo-msg-3' },
    actor_name: 'Lisa Thompson',
    room_name: 'Brand Identity Package Updates',
  },
  {
    id: 'demo-notif-4',
    user_id: 'demo-user',
    actor_id: '5',
    type: 'chat_mention',
    title: 'Mentioned',
    body: 'Hey @you, can you review this?',
    room_id: '2',
    message_id: 'demo-msg-1',
    thread_parent_id: null,
    preview: 'Hey @you, can you review this?',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    read_at: null,
    dismissed_at: null,
    archived_at: null,
    delivery_status: 'delivered',
    source_security_event_id: null,
    entity_type: null,
    entity_id: null,
    metadata: { room_id: '2', message_id: 'demo-msg-1' },
    actor_name: 'David Wilson',
    room_name: 'Mobile Banking App Security',
  },
  {
    id: 'demo-notif-7',
    user_id: 'demo-user',
    actor_id: '3',
    type: 'call_started',
    title: 'Incoming Call',
    body: 'started a video call',
    room_id: '4',
    message_id: null,
    thread_parent_id: null,
    call_session_id: 'demo-call-1',
    preview: 'started a video call',
    created_at: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
    read_at: null,
    dismissed_at: null,
    archived_at: null,
    delivery_status: 'delivered',
    source_security_event_id: null,
    entity_type: null,
    entity_id: null,
    metadata: { room_id: '4', call_session_id: 'demo-call-1' },
    actor_name: 'Emily Vendor Lead',
    room_name: 'Team Weekly Sync',
  },
];

function getDynamicDemoNotifications(userId?: string): Notification[] {
  return getDemoParticipantNotifications(userId).map(item => {
    const isDecision = item.type.startsWith('decision_');
    const decisionId = typeof item.metadata.decision_id === 'string'
      ? item.metadata.decision_id
      : null;
    return ({
    id: item.id,
    user_id: item.userId,
    actor_id: item.actorId,
    type: item.type,
    title: item.title,
    body: item.body,
    room_id: typeof item.metadata.room_id === 'string' ? item.metadata.room_id : null,
    message_id: typeof item.metadata.message_id === 'string' ? item.metadata.message_id : null,
    thread_parent_id: typeof item.metadata.thread_parent_id === 'string' ? item.metadata.thread_parent_id : null,
    decision_id: decisionId,
    preview: item.body,
    created_at: item.createdAt,
    read_at: item.readAt,
    dismissed_at: item.dismissedAt,
    archived_at: item.archivedAt,
    delivery_status: 'delivered',
    source_security_event_id: null,
    entity_type: isDecision ? 'decision' : 'project',
    entity_id: isDecision ? decisionId : item.projectId,
    metadata: item.metadata,
    });
  });
}

/**
 * Static showcase notifications represent organization-wide operator activity.
 * They are deliberately withheld from platform, scoped portal, and unassigned
 * personas; those identities receive only notifications addressed to them.
 */
export function getStaticDemoNotificationsForUser(
  user?: Pick<User, 'id' | 'account_role'> | null,
): Notification[] {
  if (!user?.id) return [];
  if (user.account_role !== 'organization_owner' && user.account_role !== 'organization_admin') {
    return [];
  }
  return DEMO_NOTIFICATIONS.map(notification => ({
    ...notification,
    user_id: user.id,
  }));
}

const NOTIFICATION_SELECT = `
  id,
  user_id,
  actor_id,
  type,
  title,
  body,
  preview,
  entity_type,
  entity_id,
  metadata,
  created_at,
  read_at,
  dismissed_at,
  archived_at,
  delivery_status,
  source_security_event_id,
  channel
`;

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useNotifications(limit: number = 30): UseNotificationsResult {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const channelRef = useRef<RealtimeChannel | null>(null);

  const unreadCount = notifications.filter(
    (n) => n.read_at === null && n.dismissed_at === null && n.archived_at === null,
  ).length;

  const fetchNotifications = useCallback(async () => {
    // Demo-only path — never used as a healthy fallback after a production failure
    if (isDemoMode) {
      if (!user?.id) {
        setNotifications([]);
        setLoading(false);
        setError(null);
        return;
      }
      setNotifications([
        ...getDynamicDemoNotifications(user.id),
        ...getStaticDemoNotificationsForUser(user),
      ].slice(0, limit));
      setLoading(false);
      setError(null);
      return;
    }

    if (!supabase) {
      setNotifications([]);
      setLoading(false);
      setError('Notifications unavailable');
      return;
    }

    if (!user?.id) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select(NOTIFICATION_SELECT)
        .eq('user_id', user.id)
        .is('archived_at', null)
        .is('dismissed_at', null)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (fetchError) {
        console.error('[useNotifications] Fetch error:', fetchError);
        setError(fetchError.message || 'Failed to load notifications');
        setNotifications([]);
      } else {
        setNotifications((data || []).map((row) => mapRow(row as NotificationRow)));
      }
    } catch (err) {
      console.error('[useNotifications] Unexpected error:', err);
      setError('Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [user, limit]);

  const markManyAsRead = useCallback(
    async (notificationIds: string[]) => {
      const uniqueNotificationIds = Array.from(new Set(notificationIds));
      if (uniqueNotificationIds.length === 0) return;

      if (isDemoMode) {
        if (!user?.id) return;
        const now = new Date().toISOString();
        uniqueNotificationIds.forEach((notificationId) => {
          updateDemoParticipantNotification(notificationId, user.id, { readAt: now });
        });
        const selectedIds = new Set(uniqueNotificationIds);
        setNotifications((prev) =>
          prev.map((n) =>
            selectedIds.has(n.id) ? { ...n, read_at: now } : n,
          ),
        );
        return;
      }

      if (!supabase || !user?.id) return;

      const now = new Date().toISOString();
      const selectedIds = new Set(uniqueNotificationIds);
      setNotifications((prev) =>
        prev.map((n) =>
          selectedIds.has(n.id) ? { ...n, read_at: now } : n,
        ),
      );

      try {
        const { error: rpcError } = await supabase.rpc('ax1_mark_notifications_read', {
          p_notification_ids: uniqueNotificationIds,
        });
        if (rpcError) {
          // Fallback: direct column update (same table; still not security_events)
          const { error: updateError } = await supabase
            .from('notifications')
            .update({ read_at: now })
            .in('id', uniqueNotificationIds)
            .eq('user_id', user.id);
          if (updateError) {
            console.error('[useNotifications] Mark selected read error:', updateError);
            await fetchNotifications();
          }
        }
      } catch (err) {
        console.error('[useNotifications] Mark selected read unexpected error:', err);
        await fetchNotifications();
      }
    },
    [fetchNotifications, user?.id],
  );

  const markAsRead = useCallback(
    async (notificationId: string) => markManyAsRead([notificationId]),
    [markManyAsRead],
  );

  const markAllAsRead = useCallback(async () => {
    if (isDemoMode) {
      if (!user?.id) return;
      const now = new Date().toISOString();
      getDemoParticipantNotifications(user.id).forEach(notification => {
        if (!notification.readAt) updateDemoParticipantNotification(notification.id, user.id, { readAt: now });
      });
      setNotifications((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
      return;
    }

    if (!supabase || !user?.id) return;

    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));

    try {
      const { error: rpcError } = await supabase.rpc('ax1_mark_all_notifications_read');
      if (rpcError) {
        const { error: updateError } = await supabase
          .from('notifications')
          .update({ read_at: now })
          .eq('user_id', user.id)
          .is('read_at', null)
          .is('archived_at', null);
        if (updateError) {
          console.error('[useNotifications] Mark all read error:', updateError);
          await fetchNotifications();
        }
      }
    } catch (err) {
      console.error('[useNotifications] Mark all read unexpected error:', err);
      await fetchNotifications();
    }
  }, [user?.id, fetchNotifications]);

  const dismiss = useCallback(
    async (notificationId: string) => {
      if (isDemoMode) {
        if (!user?.id) return;
        updateDemoParticipantNotification(notificationId, user.id, {
          dismissedAt: new Date().toISOString(),
          readAt: new Date().toISOString(),
        });
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        return;
      }
      if (!supabase) return;

      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

      try {
        const { error: rpcError } = await supabase.rpc('ax1_dismiss_notifications', {
          p_notification_ids: [notificationId],
        });
        if (rpcError) {
          const { error: updateError } = await supabase
            .from('notifications')
            .update({
              dismissed_at: new Date().toISOString(),
              read_at: new Date().toISOString(),
            })
            .eq('id', notificationId)
            .eq('user_id', user?.id ?? '');
          if (updateError) {
            console.error('[useNotifications] Dismiss error:', updateError);
            await fetchNotifications();
          }
        }
      } catch (err) {
        console.error('[useNotifications] Dismiss unexpected error:', err);
        await fetchNotifications();
      }
    },
    [fetchNotifications, user?.id],
  );

  const archive = useCallback(
    async (notificationId: string) => {
      if (isDemoMode) {
        if (!user?.id) return;
        const now = new Date().toISOString();
        updateDemoParticipantNotification(notificationId, user.id, {
          archivedAt: now,
          dismissedAt: now,
          readAt: now,
        });
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        return;
      }
      if (!supabase) return;

      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

      try {
        const { error: rpcError } = await supabase.rpc('ax1_archive_notifications', {
          p_notification_ids: [notificationId],
        });
        if (rpcError) {
          const { error: updateError } = await supabase
            .from('notifications')
            .update({
              archived_at: new Date().toISOString(),
              read_at: new Date().toISOString(),
              dismissed_at: new Date().toISOString(),
            })
            .eq('id', notificationId)
            .eq('user_id', user?.id ?? '');
          if (updateError) {
            console.error('[useNotifications] Archive error:', updateError);
            await fetchNotifications();
          }
        }
      } catch (err) {
        console.error('[useNotifications] Archive unexpected error:', err);
        await fetchNotifications();
      }
    },
    [fetchNotifications, user?.id],
  );

  useEffect(() => {
    fetchNotifications();

    if (isDemoMode) {
      return subscribeDemoProjectParticipants(() => {
        void fetchNotifications();
      });
    }

    if (!supabase || !user?.id) {
      return;
    }

    const channel = supabase
      .channel(nextPostgresChangesChannelTopic(`notifications:${user.id}`))
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const mapped = mapRow(payload.new as NotificationRow);
          if (mapped.archived_at || mapped.dismissed_at) return;
          setNotifications((prev) => [mapped, ...prev].slice(0, limit));
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const mapped = mapRow(payload.new as NotificationRow);
          if (mapped.archived_at || mapped.dismissed_at) {
            setNotifications((prev) => prev.filter((n) => n.id !== mapped.id));
            return;
          }
          setNotifications((prev) => prev.map((n) => (n.id === mapped.id ? mapped : n)));
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const deleted = payload.old as { id: string };
          setNotifications((prev) => prev.filter((n) => n.id !== deleted.id));
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current && supabase) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, limit, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markManyAsRead,
    markAllAsRead,
    dismiss,
    archive,
    refresh: fetchNotifications,
  };
}

export { isSecurityDerivedNotificationType };
export default useNotifications;
